import React, { useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { AlertTriangle, Shield, Loader2 } from 'lucide-react';
import objectiveService from '../../services/objectiveService';
import { documentApi } from '../../services/api';
import { UnifiedClinicalFindings } from './UnifiedClinicalFindings';
import type { UnifiedClinicalFindingsHandle } from './UnifiedClinicalFindings';
import { sanitizeEnumValue } from '../../utils/displayUtils';
import { DerivedOutputBanner } from './DerivedOutputBanner';

interface ClinicalStandardsTabProps {
  caseId: string;
}

function sev(s: string | undefined): string { return sanitizeEnumValue(s) || 'info'; }

// --- Tier-aligned insights ---

interface TierInsights {
  priority: number;
  further: number;
  context: number;
  summaryLine: string;
}

/** Read documentsAnalysed from metadata, trying both British and American spellings */
function readDocCount(meta: any): number {
  if (!meta) return 0;
  return meta.documentsAnalysed || meta.documentsAnalyzed || 0;
}

function extractTierInsights(socContent: any | null, compContent: any | null): TierInsights {
  let priority = 0;
  let further = 0;
  let context = 0;
  let documentsAnalysed = 0;

  if (socContent) {
    for (const d of (socContent.clinicalDecisions || [])) {
      const a = sev(d.assessment);
      if (a === 'breach') priority++;
      else if (a === 'deviation') further++;
      else context++;
    }
    priority += (socContent.areasOfConcern || []).length;
    documentsAnalysed = readDocCount(socContent.metadata);
  }

  if (compContent) {
    for (const d of (compContent.decisionPoints || [])) {
      if (d.analysis?.wasChosenApproachReasonable === false) priority++;
      else context++;
    }
    further += (compContent.missedOpportunities || []).length;
    if (!documentsAnalysed) {
      documentsAnalysed = readDocCount(compContent.metadata);
    }
  }

  const total = priority + further + context;
  const docSuffix = documentsAnalysed > 0 ? ` across ${documentsAnalysed} documents` : '';
  const summaryLine = priority > 0
    ? `${total} clinical decisions analysed${docSuffix}. ${priority} ${priority === 1 ? 'area' : 'areas'} flagged for priority review.`
    : total > 0
      ? `${total} clinical decisions analysed${docSuffix}. No areas flagged for priority review.`
      : documentsAnalysed > 0
        ? `Analysis based on ${documentsAnalysed} documents.`
        : 'Analysis complete.';

  return { priority, further, context, summaryLine };
}

// --- Component ---

export const ClinicalStandardsTab: React.FC<ClinicalStandardsTabProps> = ({ caseId }) => {
  const findingsRef = useRef<UnifiedClinicalFindingsHandle>(null);

  const { data: objectives = [] } = useQuery(
    ['objectives', caseId],
    () => objectiveService.getObjectives(caseId),
    {
      enabled: !!caseId,
      refetchInterval: (data) => {
        const soc = data?.find(o => o.type === 'STANDARD_OF_CARE');
        const comp = data?.find(o => o.type === 'COMPARATIVE_ANALYSIS');
        return (soc?.status === 'PROCESSING' || comp?.status === 'PROCESSING') ? 2000 : false;
      },
    }
  );

  const socObj = objectives.find(o => o.type === 'STANDARD_OF_CARE');
  const compObj = objectives.find(o => o.type === 'COMPARATIVE_ANALYSIS');

  const { data: socOutput } = useQuery(
    ['output', caseId, socObj?.id],
    () => objectiveService.getOutput(caseId, socObj!.id),
    { enabled: !!socObj && socObj.status === 'UP_TO_DATE' && !!socObj.lastGeneratedAt, retry: false }
  );
  const { data: compOutput } = useQuery(
    ['output', caseId, compObj?.id],
    () => objectiveService.getOutput(caseId, compObj!.id),
    { enabled: !!compObj && compObj.status === 'UP_TO_DATE' && !!compObj.lastGeneratedAt, retry: false }
  );

  const { data: documents = [] } = useQuery(
    ['documents', caseId],
    () => documentApi.getCaseDocuments(caseId),
    { enabled: !!caseId }
  );

  const socContent = socObj?.status === 'UP_TO_DATE' && socOutput ? (socOutput.content as any) : null;
  const compContent = compObj?.status === 'UP_TO_DATE' && compOutput ? (compOutput.content as any) : null;
  const insights = useMemo(() => extractTierInsights(socContent, compContent), [socContent, compContent]);
  const hasAnyData = socContent || compContent;

  function getStatus(obj: any): 'loading' | 'error' | 'ready' | 'pending' {
    if (!obj) return 'pending';
    if (obj.status === 'PROCESSING') return 'loading';
    if (obj.status === 'ERROR') return 'error';
    if (obj.status === 'UP_TO_DATE') return 'ready';
    return 'pending';
  }

  const socStatus = getStatus(socObj);
  const compStatus = getStatus(compObj);
  const anyLoading = socStatus === 'loading' || compStatus === 'loading';
  const bothPending = socStatus === 'pending' && compStatus === 'pending';
  const bothProcessing = socStatus === 'loading' && compStatus === 'loading';

  return (
    <div className="p-6 h-full overflow-auto space-y-6">
      {/* Header */}
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100 mb-1">Clinical Standards</h3>
        <p className="text-sm text-gray-600 dark:text-medical-400">
          Review of clinical decisions against expected standards and alternative approaches.
        </p>
      </div>

      <DerivedOutputBanner
        caseId={caseId}
        analysisLabel="clinical standards review"
        evaluatePrompt="I have corrected some chronology events since the clinical standards review was generated. Review the edited events and assess whether the corrections affect the standard-of-care analysis, alternative treatment considerations, or compliance findings."
      />

      {/* Key Insights */}
      {hasAnyData && (
        <div className="bg-gray-50 dark:bg-medical-800/50 border border-gray-200 dark:border-medical-700 rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase text-gray-500 dark:text-medical-400 tracking-wide">
            <Shield className="w-3.5 h-3.5" />
            Key Insights
          </div>

          <p className="text-sm text-gray-700 dark:text-medical-300">{insights.summaryLine}</p>

          {anyLoading && (
            <div className="flex flex-wrap gap-1.5">
              {socStatus === 'loading' && (
                <span className="text-xs text-gray-400 dark:text-medical-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Standards analysis loading…
                </span>
              )}
              {compStatus === 'loading' && (
                <span className="text-xs text-gray-400 dark:text-medical-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Comparative analysis loading…
                </span>
              )}
            </div>
          )}

          {/* Tier stat cards — clickable to scroll */}
          <div className="grid grid-cols-3 gap-3">
            <TierCard
              label="Priority Review"
              description="Areas flagged as most likely to warrant further attention"
              count={insights.priority}
              color={insights.priority > 0 ? 'red' : 'green'}
              loading={anyLoading && !hasAnyData}
              onClick={() => findingsRef.current?.scrollToSection('priority')}
            />
            <TierCard
              label="Further Consideration"
              description="Variations and alternatives that may warrant review"
              count={insights.further}
              color={insights.further > 0 ? 'amber' : 'green'}
              loading={anyLoading && !hasAnyData}
              onClick={() => findingsRef.current?.scrollToSection('further')}
            />
            <TierCard
              label="Reviewed & Noted"
              description="Decisions reviewed with no significant concerns"
              count={insights.context}
              color="green"
              loading={anyLoading && !hasAnyData}
              onClick={() => findingsRef.current?.scrollToSection('context')}
            />
          </div>
        </div>
      )}

      {/* Loading / pending states when no data yet */}
      {!hasAnyData && bothProcessing && <LoadingState label="Clinical standards analysis" />}
      {!hasAnyData && bothPending && <PendingState />}
      {!hasAnyData && !bothProcessing && !bothPending && (socStatus === 'error' || compStatus === 'error') && (
        <ErrorState label="Clinical standards analysis" />
      )}

      {/* Unified findings view */}
      {hasAnyData && (
        <UnifiedClinicalFindings
          ref={findingsRef}
          socContent={socContent}
          compContent={compContent}
          documents={documents}
        />
      )}
    </div>
  );
};

// --- Shared sub-components ---

const TIER_COLORS = {
  red: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30',
  amber: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30',
  green: 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30',
} as const;

function TierCard({ label, description, count, color, loading, onClick }: {
  label: string;
  description: string;
  count: number;
  color: keyof typeof TIER_COLORS;
  loading?: boolean;
  onClick?: () => void;
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-medical-600 p-3 animate-pulse">
        <div className="h-6 w-8 bg-gray-200 dark:bg-medical-700 rounded mb-1" />
        <div className="h-3 w-16 bg-gray-200 dark:bg-medical-700 rounded" />
      </div>
    );
  }
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border p-3 text-left transition-colors cursor-pointer ${TIER_COLORS[color]}`}
    >
      <p className="text-2xl font-bold">{count}</p>
      <p className="text-xs font-medium opacity-75">{label}</p>
      <p className="text-[10px] opacity-60 mt-1 leading-tight">{description}</p>
    </button>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="text-center py-12">
      <Loader2 className="w-10 h-10 text-cyan-500 mx-auto mb-3 animate-spin" />
      <p className="text-sm text-gray-600 dark:text-medical-400">{label} is being generated…</p>
    </div>
  );
}

function ErrorState({ label }: { label: string }) {
  return (
    <div className="text-center py-12">
      <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
      <p className="text-sm text-gray-600 dark:text-medical-400">{label} failed during extraction.</p>
    </div>
  );
}

function PendingState() {
  return (
    <div className="text-center py-12">
      <Shield className="w-10 h-10 text-gray-300 dark:text-medical-600 mx-auto mb-3" />
      <p className="text-sm text-gray-600 dark:text-medical-400 max-w-md mx-auto">
        The uploaded evidence did not contain sufficient information to generate a clinical standards analysis.
      </p>
    </div>
  );
}
